<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import Button from "./ui/Button.vue";
import Icon from "./ui/Icon.vue";
import Dropdown from "./ui/Form/Dropdown.vue";
import Checkbox from "./ui/Form/Checkbox.vue";
import FeatureFlagList from "./ui/FeatureFlagList.vue";
import { useSettings } from "~/store/settings";
import { getCookie, setCookie } from "~/utils/cookie-util";

let environments = {};
try {
	// @ts-ignore
	environments = (await import("@/environments.js")).default;
} catch (error) {}

const store = useSettings();

const environmentsList = [...Object.keys(environments), "Default"];
const regions = ["GH", "TZ", "MW", "NG", "ZA", "KE", "UG", "ZM", "MZ", "BW", "SZA"];
const { countryISOCode } = useISOCode();

const cookie = useCookie("DEV--country");
const translationsCookie = useCookie("DEV--translations");
const envCookie = useCookie("DEV--environment");
const nativeAppCookie = useCookie("isNativeApp");
const forceEnglishCookie = useCookie("DEV--forceEnglish");
const isDataFreeCookie = useCookie("isDataFree");

const region = ref(countryISOCode.value || regions[0]);
const environment = ref(envCookie.value || environmentsList[0]);
const translationsEnabled = ref(Boolean(translationsCookie.value) || false);
const isNativeAppEnabled = ref<boolean>(Boolean(nativeAppCookie.value) || false);
const forceEnglish = ref<boolean>(Boolean(forceEnglishCookie.value) || false);
const isDataFree = ref<boolean>(Boolean(isDataFreeCookie.value) || false);

const open = ref(false);
const featureFlagListOpen = ref(false);
const twoFingerTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const openFeatureFlags = () => {
	featureFlagListOpen.value = !featureFlagListOpen.value;
};

const handleKeydown = (e: KeyboardEvent) => {
	if (e.shiftKey && e.key === "?") open.value = !open.value;
};

const setNativeApp = () => {
	nativeAppCookie.value = isNativeAppEnabled.value.toString();

	if (isNativeAppEnabled.value) {
		store.setNativeAppSettings(Boolean(true), String(store.appType), Boolean(true), String(store.isSynapseV2), "true");
	} else {
		store.setNativeAppSettings(Boolean(false), String(store.appType), Boolean(false), String(store.isSynapseV2), "true");
	}
};

const setTranslations = async () => {
	store.showTranslations = !translationsEnabled.value;
	translationsCookie.value = translationsEnabled.value.toString();
	await initialiseLocale();
};

onMounted(() => {
	document.addEventListener("keydown", handleKeydown);
	window.addEventListener("touchstart", onTouchStart);
	window.addEventListener("touchend", onTouchEnd);
	setTranslations();
	if (forceEnglishCookie.value && store.getCultureConfig?.cultureCode !== "en-US") setForceEnglish();
});

onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown);
});

const handleClick = () => {
	cookie.value = region.value;
	envCookie.value = environment.value;
	window.location.reload();
};

const setForceEnglish = async () => {
	forceEnglishCookie.value = forceEnglish.value.toString();
	if (countryISOCode.value !== "en-US" && forceEnglish.value) {
		store.setLocale("en-US");
		await initialiseLocale();
	}
};

const setDataFree = async () => {
	isDataFreeCookie.value = isDataFree.value.toString();
	setCookie("isDataFree", true, isDataFreeCookie.value ? 1 : 0);
	window.location.reload();
};
const onTouchStart = (e: TouchEvent) => {
	if (e.touches.length === 2) {
		twoFingerTimer.value = setTimeout(() => {
			open.value = true;
		}, 800);
	}
};
const onTouchEnd = () => {
	if (twoFingerTimer.value) {
		clearTimeout(twoFingerTimer.value);
		twoFingerTimer.value = null;
	}
};
</script>

<template>
	<Teleport to="body">
		<div
			v-if="open"
			class="dark fixed bottom-0 z-[70] flex w-screen justify-evenly items-center bg-dark-800 text-light-50 p-2 gap-3 md:gap-5 flex-col md:flex-row"
		>
			<strong class="whitespace-nowrap">Dev Console</strong>

			<div class="flex w-full max-w-2xl gap-3">
				<Dropdown
					v-if="regions"
					label="Country"
					v-model="region"
					:options="
						regions.map((r) => {
							return { name: r, value: r };
						})
					"
				/>

				<Dropdown
					v-if="environmentsList"
					label="Environments"
					v-model="environment"
					:options="
						environmentsList.map((r) => {
							return { name: r, value: r };
						})
					"
				/>
			</div>

			<div class="w-full flex items-center gap-3 justify-evenly md:justify-start flex-wrap !z-[60]">
				<Button label="Feature Flags" @click="openFeatureFlags()" :size="'small'" :variant="'identity'" />
				<Checkbox v-model="translationsEnabled" :label="'Disable Translations'" class="whitespace-nowrap" @update:model-value="setTranslations()" />
				<Checkbox v-model="isNativeAppEnabled" :label="'Native App'" class="whitespace-nowrap" @update:model-value="setNativeApp()" />
				<Checkbox v-model="forceEnglish" :label="'Force English'" class="whitespace-nowrap" @update:model-value="setForceEnglish()" />
				<Checkbox v-model="isDataFree" :label="'DataFree'" class="whitespace-nowrap" @update:model-value="setDataFree()" />
			</div>
			<Button
				label="Submit"
				:size="'small'"
				:variant="'identity'"
				@click="handleClick()"
				:disabled="region === countryISOCode && envCookie === environment"
			/>
			<Icon
				:path="mdiClose"
				class="md:w-12 md:mr-2 aspect-square fill-light-50 absolute top-2 right-2 md:top-0 md:right-0 md:relative w-6 cursor-pointer"
				@click="open = false"
			/>
		</div>
		<FeatureFlagList v-if="open" :open="featureFlagListOpen && open" />
	</Teleport>
</template>
